<template>
  <div class="screenshot-wrap flex flex-col gap-2" v-if="user">
    <div class="img-container relative aspect-[16/10] w-full overflow-hidden rounded-chip bg-input">
      <div class="imageHolder" v-if="!realImageLoaded || imageHidden">
        <img
          v-if="thumbHashUrl"
          class="screenshot thumbhash-placeholder bg-black"
          :src="thumbHashUrl"
          alt=""
        />
        <div v-else-if="!imageHidden" class="uploadingImage">
          <Loader />
        </div>
      </div>
      <img
        v-if="canLoadOriginal"
        v-show="realImageLoaded"
        @mouseenter="preloadZoomSrcImage"
        :ref="attachZoom"
        class="screenshot bg-black"
        :src="thumbHashUrl ? thumbUrl : originalUrl"
        :data-zoom-src="originalUrl"
        @load="screenshotLoaded"
        @error="setOriginalUrl"
      />
      <HoverableTooltip
        v-if="imageHidden"
        class="screenshot-lock"
        :text="hiddenScreenshotTooltip"
      >
        <span
          class="lock-badge"
          tabindex="0"
          :aria-label="hiddenScreenshotTooltip"
        >
          <IconLockClosed16Solid class="lock-badge__icon" />
        </span>
      </HoverableTooltip>
      <a href="#"><UserAvatar withDayoffBadge class="avatar" :user="getUser(screenshot.user.id)" /></a>
      <ConfirmButton
        v-if="!reportView && screenshot.user.id === user.id"
        class="delete"
        :popupTitle="$t('task.delete_screenshot_title')"
        :popupSubTitle="$t('task.delete_screenshot_subtitle')"
        @confirm="onDelete"
      >
        <template #menu-trigger>
          <button
            type="button"
            class="flex size-7 cursor-pointer items-center justify-center rounded-chip border-0
                   bg-[rgba(18,18,24,0.55)] p-0 text-white transition-colors duration-150
                   hover:bg-red"
          >
            <IconTrash16Solid class="size-3.5" />
          </button>
        </template>
      </ConfirmButton>
    </div>
    <div class="stats flex h-5 items-center gap-2 px-0.5 text-label font-medium text-text-muted select-none">
      <div class="screen__info-item">
        <i
          :class="`tr color-ico-${getActivityIcon()}
            tr-activity-level-${getActivityIcon()} genfont`"
          :title="`${$t('task.screenshot_activity')}: ${activityPercent}%`"
        />
      </div>
      <div
        v-if="activityTitlesList.length"
        :data-title="activityTitle"
        class="tooltip-activity flex items-center gap-1"
      >
        <i class="tr tr-processes-opened genfont"></i>
        {{ activityTitlesList.length }}
      </div>
      <div class="screen-browser" v-else>
        <i class="tr tr-browser"></i>
      </div>
      <Time :time="screenshot.capturedAt" class="ml-auto font-bold" />
    </div>
  </div>
</template>

<script setup>
import Vue, { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import mediumZoom from 'medium-zoom';
import Time from '@/components/elements/Time';
import ConfirmButton from '@/components/elements/ConfirmButton';
import HoverableTooltip from '@/components/elements/HoverableTooltip.vue';
import { IconLockClosed16Solid, IconTrash16Solid } from '@iconify-prerendered/vue-heroicons';
import { thumbHashToImageDataUrl } from '@/store/utils';

const emit = defineEmits(['delete']);

const props = defineProps({
  reportView: {
    type: Boolean,
    default: false,
  },
  screenshot: Object,
  task: Object,
  user: Object,
});

const img = ref(null);
const store = useStore();
const { t } = useI18n();
const getUser = store.getters['user/getUser'];
const projectsList = computed(() => store.state.project.projectsList || []);

const realImageLoaded = ref(false);
const input = ref(
  props.screenshot.input && typeof props.screenshot.input === 'string'
    ? JSON.parse(props.screenshot.input)
    : {
        m: 0,
        k: 0,
        w: 0,
        a: 1,
      }
);
const titlesList = ref(
  props.screenshot.titlesList && typeof props.screenshot.titlesList === 'string'
    ? JSON.parse(props.screenshot.titlesList)
    : {}
);
const capturedTitle = ref(props.screenshot.capturedTitle);
const thumbHashUrl = computed(() => thumbHashToImageDataUrl(props.screenshot.thumbHash));
const imageHidden = computed(() => props.screenshot.imageHidden || (!props.screenshot.fileId && !props.screenshot.data));
const canLoadOriginal = computed(() => !imageHidden.value && (!!props.screenshot.fileId || !!props.screenshot.data));
const screenshotProjectName = computed(() => {
  const projectId = props.screenshot.projectId || props.task?.projectId;
  return props.screenshot.projectName
    || props.screenshot.project?.name
    || props.task?.project?.name
    || props.task?.projectName
    || projectsList.value.find(project => project.id === projectId)?.name
    || '';
});
const hiddenScreenshotTooltip = computed(() => {
  const projectName = screenshotProjectName.value;
  if (!projectName) {
    return t('ui.hidden_screenshot_tooltip_no_project');
  }
  return t('ui.hidden_screenshot_tooltip_in_project', { projectName });
});

const baseUrl = computed(() => {
  return `${window.s3base}/screenshots/${props.screenshot.taskId}/${props.screenshot.userId}/`;
});

const thumbUrl = computed(() => {
  if (!props.screenshot.fileId) {
    return '';
  }
  return `${baseUrl.value}200x100/${props.screenshot.fileId}.jpeg`;
});

const originalUrl = computed(() => {
  if (props.screenshot.data) {
    return props.screenshot.data;
  }
  if (!props.screenshot.fileId) {
    return '';
  }
  return `${baseUrl.value}${props.screenshot.fileId}.jpeg`;
});

const activityTitlesList = computed(() => {
  if (titlesList.value && capturedTitle.value !== 'inBrowser') {
    const apps = Object.keys(titlesList.value).map((key) => ({
      w: key,
      t: titlesList.value[key],
    }));
    return apps.sort((a, b) => b.t - a.t);
  }
  return [];
});

const preloadedImages = {};

function preloadZoomSrcImage() {
  if (canLoadOriginal.value && originalUrl.value && !preloadedImages[originalUrl.value]) {
    preloadedImages[originalUrl.value] = true;
    const zoomImg = new Image();
    zoomImg.src = originalUrl.value;
  }
}

const sumTitlesList = computed(() => {
  if (titlesList.value && capturedTitle.value !== 'inBrowser') {
    return Object.values(titlesList.value).reduce((acc, v) => acc + v, 0);
  }
  return 0;
});

const activityTitle = computed(() => {
  return activityTitlesList.value.reduce(
    (acc, item) =>
      `${acc + item.w}: ${+((item.t / sumTitlesList.value) * 100).toFixed(1)}%\n`,
    ''
  );
});


const activityPercent = computed(() => {
  if (input.value?.a) {
    return Math.ceil((1 - input.value.a) * 100);
  }

  if (input.value?.inactive !== undefined) {
    return Math.ceil(
      100 - (input.value.inactive / props.screenshot.actualTimeSeconds) * 100
    );
  }
  return 1;
});

let zoom = null

function getZoom() {
  if (zoom === null) {
    zoom = mediumZoom({
      background: 'rgba(0,0,0,0.7)',
      margin: 30,
    })
  }

  return zoom
}

function attachZoom(ref) {
  const image = ref;
  img.value = image;
  const zoom = getZoom()

  if (image && canLoadOriginal.value) {
    zoom.attach(image)
  } else {
    zoom.detach()
  }
}

const screenshotLoaded = () => {
  realImageLoaded.value = true;
};

const setOriginalUrl = () => {
  if (!originalUrl.value) {
    return;
  }
  const screenshotEl = img.value;
  if (!screenshotEl) {
    return;
  }
  if (screenshotEl.src !== originalUrl.value) {
    screenshotEl.src = originalUrl.value;
  }
};

const getActivityIcon = () => {
  if (activityPercent.value < 25) {
    return 1;
  }
  if (activityPercent.value < 50) {
    return 2;
  }
  if (activityPercent.value < 75) {
    return 3;
  }

  return 4;
};

const onDelete = () => {
  emit('delete', props.screenshot);
};
</script>

<style lang="scss" scoped>


:deep(.loader[class]) {
  transform: scale(0.25);
  margin-bottom: 0;
  margin-top: -18px;
  margin-left: 75px;
  position: absolute;
}
.genfont {
  margin-bottom: -3px;
}

.uploadingImage {
  top: 2.375rem;
  position: absolute;
}
.stats {
  i {
    &.color-ico-1 {
      color: var(--text-muted);
    }
    &.color-ico-2 {
      color: var(--amber);
    }
    &.color-ico-3,
    &.color-ico-4 {
      color: var(--green);
    }
  }
}
.img-container {
  div.userAvatar {
    cursor: auto;
  }
  > img,
  .thumbhash-placeholder {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
  }
}
.imageHolder {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
}
.thumbhash-placeholder[class] {
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  object-fit: cover;
}
.screenshot-lock {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  transform: translate(-50%, -50%);
}
.lock-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(18, 18, 24, 0.5);
  backdrop-filter: blur(4px);
  border-radius: 50%;
  outline: none;
}
.lock-badge__icon {
  width: 1.125rem;
  height: 1.125rem;
}
.screenshot {
  max-height: 100%;
  max-width: 100%;
}
.avatar[class] {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  box-shadow: 0 2px 4px color-mix(in srgb, var(--text-muted) 35%, transparent);
  border-radius: 50%;
}
.delete {
  position: absolute !important;
  top: 0.5rem;
  right: 0.5rem;
  opacity: 0.35;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
}
.medium-zoom-image--hidden {
  width: 170px !important;
  height: auto !important;
  object-fit: contain;
}
.medium-zoom-image--opened {
  object-fit: contain !important;
  height: auto !important;
  border-radius: none;
}
.tr-process {
  height: 16px;
}
.screen-browser {
  display: flex;
  justify-content: center;
  align-items: center;
}
.tooltip-activity {
  &[data-title] {
    position: relative;
  }

  &[data-title]:after {
    z-index: var(--z-popover);
    content: attr(data-title);
    position: absolute;
    left: 50%;
    bottom: -10px; /* put it on the top */
    background-color: var(--popover-solid);
    box-shadow: 1px 1px 5px rgb(0 0 0 / 35%);
    width: max-content;
    opacity: 0;
    display: none;
    padding: 5px 10px;
    white-space: pre;
    -webkit-transition: opacity 0.15s ease-in-out; /* 👈 Change the time to meet your requirements. */
  }

  &[data-title]:hover:after {
    opacity: 1;
    display: initial;
  }
}
</style>
